import React from 'react';
// Get weather icon based on condition and time of day
const getWeatherIconFixed = (conditionText, isDay) => {
  if (!conditionText) return isDay === 1 ? '/sunny.png' : '/clear.png';
  
  const normalizedCondition = conditionText.toLowerCase();
  
  // Fix: Check for snow conditions FIRST, before rain
  if (normalizedCondition.includes('snow')) {
    return '/snow.png';
  }
  
  if (normalizedCondition.includes('rain') || normalizedCondition.includes('drizzle') || normalizedCondition.includes('shower')) {
    if (normalizedCondition.includes('light')) return '/lightrain.png';
    if (normalizedCondition.includes('heavy')) return '/heavyrain.png';
    if (normalizedCondition.includes('freezing')) return '/sleet.png';
    return '/heavyrain.png';
  }
  
  if (normalizedCondition.includes('thunder')) {
    return '/thunderstorm.png';
  }
  
  if (normalizedCondition.includes('fog') || normalizedCondition.includes('mist')) {
    return '/fog.png';
  }
  
  if (normalizedCondition.includes('cloudy') || normalizedCondition.includes('overcast')) {
    if (normalizedCondition.includes('partly')) {
      return isDay === 1 ? '/partlycloudy.png' : '/cloudynight.png';
    }
    return isDay === 1 ? '/cloudy.png' : '/vcloudynight.png';
  }
  
  return isDay === 1 ? '/sunny.png' : '/clear.png';
};

// Fix for snow icon issue - override the icon function locally
const getWeatherIconFixedLocal = (conditionText, isDay) => {
  if (!conditionText) return isDay === 1 ? '/sunny.png' : '/clear.png';
  
  const normalizedCondition = conditionText.toLowerCase();
  
  // Fix: Check for snow conditions FIRST, before rain/shower logic
  if (normalizedCondition.includes('snow')) {
    return '/snow.png';
  }
  
  // Check for mostly/mainly/almost clear conditions
  if (normalizedCondition.includes('mostly clear') || 
      normalizedCondition.includes('mainly clear') || 
      normalizedCondition.includes('almost clear')) {
    return '/msunny.png';
  }
  
  if (normalizedCondition.includes('rain') || normalizedCondition.includes('drizzle') || normalizedCondition.includes('shower')) {
    if (normalizedCondition.includes('light')) return '/lightrain.png';
    if (normalizedCondition.includes('heavy')) return '/heavyrain.png';
    if (normalizedCondition.includes('freezing')) return '/sleet.png';
    return '/heavyrain.png';
  }
  
  if (normalizedCondition.includes('thunder')) {
    return '/thunderstorm.png';
  }
  
  if (normalizedCondition.includes('fog') || normalizedCondition.includes('mist')) {
    return '/fog.png';
  }
  
  if (normalizedCondition.includes('cloudy') || normalizedCondition.includes('overcast')) {
    if (normalizedCondition.includes('partly')) {
      return isDay === 1 ? '/partlycloudy.png' : '/cloudynight.png';
    }
    return isDay === 1 ? '/cloudy.png' : '/vcloudynight.png';
  }
  
  return isDay === 1 ? '/sunny.png' : '/clear.png';
};

const DailyForecast = ({ weather }) => {
  // Debug data - log to console (must be called before any returns)
  React.useEffect(() => {
    if (weather) {
      // SIMPLE DEBUG - just what we need
      console.log('=== SIMPLE DEBUG ===');
      console.log('First forecast date (should be Today):', weather.forecast?.forecastday?.[0]?.date);
      console.log('All forecast dates:', weather.forecast?.forecastday?.map(d => d.date));
    }
  }, [weather]);

  if (!weather || !weather.forecast || !weather.forecast.forecastday) {
    return null;
  }

  // Simple function to capitalize weather condition text
  const formatCondition = (text) => {
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // FIXED: Just use the first forecast day as "Today" - no timezone conversion needed
  const getForecastDays = () => {
    // The API already returns dates in the location's local time
    // So just use all forecast days as-is, up to 14 days
    return weather.forecast.forecastday.slice(0, 14);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return `${day} ${month}`;
  };

  // Get day name
  const getDayName = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };

  // Get wind direction from degrees
  const getWindDirection = (degrees) => {
    if (!degrees && degrees !== 0) return '';
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    return directions[Math.round(degrees / 22.5) % 16];
  };

  // Get AQI data for a specific date - Updated to calculate from hourly data
  const getAQIForDate = (dateString) => {
    console.log(`Getting AQI for date: ${dateString}`);
    
    // Only calculate AQI for today and next 3 days (4 days total)
    const today = new Date().toISOString().split('T')[0];
    const targetDate = new Date(dateString + 'T00:00:00');
    const todayDate = new Date(today + 'T00:00:00');
    const daysDifference = Math.floor((targetDate - todayDate) / (1000 * 60 * 60 * 24));
    
    if (daysDifference > 3) {
      console.log(`Date ${dateString} is beyond 4-day limit, skipping AQI calculation`);
      return {
        value: null,
        category: null,
        color: '#9ca3af',
        source: 'beyond_limit'
      };
    }

    // Calculate from hourly data
    if (weather.hourly_aqi && weather.hourly_aqi.forecast) {
      console.log('Found hourly_aqi.forecast, calculating daily AQI from hourly data');
      
      // Find all hourly readings for this date
      const hourlyReadings = weather.hourly_aqi.forecast.filter(hour => {
        if (hour.time && hour.time.startsWith(dateString)) {
          return true;
        }
        if (hour.date && hour.date.startsWith(dateString)) {
          return true;
        }
        return false;
      });
      
      console.log(`Found ${hourlyReadings.length} hourly readings for ${dateString}:`, hourlyReadings);
      
      if (hourlyReadings.length > 0) {
        // Extract AQI values and filter out null/undefined values
        const aqiValues = hourlyReadings
          .map(hour => hour.AQI || hour.aqi || hour.airQualityIndex)
          .filter(value => value !== null && value !== undefined && value > 0);
        
        console.log(`Valid AQI values for ${dateString}:`, aqiValues);
        
        if (aqiValues.length > 0) {
          // Sort values in descending order to get the HIGHEST readings first
          const sortedValues = aqiValues.sort((a, b) => b - a);
          
          // Take the HIGHEST 4 readings, or all available if less than 4
          const topValues = sortedValues.slice(0, Math.min(4, sortedValues.length));
          
          // Calculate average: (sum of highest readings) / (number of readings used)
          // If 4+ readings available: (highest 4 values) / 4
          // If <4 readings available: (all values) / (actual count)
          const averageAQI = Math.round(topValues.reduce((sum, value) => sum + value, 0) / topValues.length);
          
          console.log(`Sorted AQI values (highest first): ${sortedValues}`);
          console.log(`Using top ${topValues.length} HIGHEST values: ${topValues}`);
          console.log(`Formula: (${topValues.join(' + ')}) / ${topValues.length} = ${averageAQI}`);
          
          const result = {
            value: averageAQI,
            category: getAQICategory(averageAQI),
            color: getAQIColor(averageAQI),
            source: 'calculated_hourly',
            readings_used: topValues.length,
            top_values: topValues,
            all_values: sortedValues
          };
          console.log(`Calculated AQI result for ${dateString}:`, result);
          return result;
        }
      }
    }

    // Try alternative data structures
    if (weather.hourly_aqi) {
      console.log('hourly_aqi structure:', weather.hourly_aqi);
      // Check if it's a flat array
      if (Array.isArray(weather.hourly_aqi)) {
        console.log('hourly_aqi is an array, first item:', weather.hourly_aqi[0]);
        
        // Try to filter by date if it's a flat array
        const hourlyReadings = weather.hourly_aqi.filter(hour => {
          if (hour.time && hour.time.startsWith(dateString)) {
            return true;
          }
          if (hour.date && hour.date.startsWith(dateString)) {
            return true;
          }
          return false;
        });
        
        if (hourlyReadings.length > 0) {
          const aqiValues = hourlyReadings
            .map(hour => hour.AQI || hour.aqi || hour.airQualityIndex)
            .filter(value => value !== null && value !== undefined && value > 0);
          
          if (aqiValues.length > 0) {
            // Sort values in descending order to get the HIGHEST readings first
            const sortedValues = aqiValues.sort((a, b) => b - a);
            
            // Take the HIGHEST 4 readings, or all available if less than 4
            const topValues = sortedValues.slice(0, Math.min(4, sortedValues.length));
            
            // Calculate average: (sum of highest readings) / (number of readings used)
            const averageAQI = Math.round(topValues.reduce((sum, value) => sum + value, 0) / topValues.length);
            
            console.log(`Calculated AQI from flat array for ${dateString}: ${averageAQI} (using ${topValues.length} highest values: ${topValues})`);
            
            return {
              value: averageAQI,
              category: getAQICategory(averageAQI),
              color: getAQIColor(averageAQI),
              source: 'calculated_hourly_flat',
              readings_used: topValues.length,
              top_values: topValues,
              all_values: sortedValues
            };
          }
        }
      }
    }

    // Fallback when no data found
    console.log(`No hourly AQI data found for ${dateString}, using fallback`);
    return {
      value: null,
      category: null,
      color: '#9ca3af',
      source: 'no_data'
    };
  };

  // Convert AQI value to simplified category
  const getAQICategory = (aqi) => {
    if (!aqi || aqi === 0) return null; // FIXED: Return null instead of 'Good' when no data
    if (aqi < 100) return 'Good';
    if (aqi < 150) return 'OK';
    if (aqi < 200) return 'Poor';
    return 'Bad';
  };

  // Get color for simplified AQI value
  const getAQIColor = (aqi) => {
    if (!aqi || aqi === 0) return '#9ca3af'; // Gray when no data
    if (aqi < 100) return '#10b981';  // Green - Good
    if (aqi < 150) return '#f59e0b'; // Orange - OK
    if (aqi < 200) return '#f97316'; // Orange-Red - Poor
    return '#ef4444'; // Red - Bad
  };

  const forecastDays = getForecastDays();
  // FIXED: The first day from API is always "today" in the location's time
  const firstDayDate = forecastDays[0]?.date;

  // Dynamic grid template based on precipitation data
  const getGridTemplate = () => {
    // Always use the same grid template regardless of precipitation data
    return {
      desktop: '1fr 1fr 100px 20px 1fr 0.65fr 0.65fr 1.3fr 1.3fr 1.3fr',
      mobile: '1fr 1fr 60px 15px 0.8fr 0.5fr 0.5fr',
      small: '0.8fr 0.8fr 50px 10px 0.6fr 0.3fr 0.3fr'
    };
  };

  const gridTemplates = getGridTemplate();

  return (
    <div style={{ 
      width: '100%', 
      marginBottom: '2rem'
    }}>
      <style>
        {`
          .forecast-container {
            width: 100%;
            background: black;
            border-radius: 0.5rem;
            overflow: hidden;
          }
          
          .forecast-title {
            font-size: 1.25rem;
            font-weight: bold;
            margin-bottom: 1.5rem;
            text-align: center;
            color: white;
          }
          
          .forecast-row {
            display: grid;
            grid-template-columns: ${gridTemplates.desktop};
            align-items: center;
            padding: 1rem;
            border-bottom: 1px solid #374151;
            background: black;
            transition: background-color 0.2s;
          }
          
          .forecast-row:hover {
            background: black;
          }
          
          .forecast-row:last-child {
            border-bottom: none;
          }
          
          .day-info {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
          
          .day-name {
            font-weight: 600;
            font-size: 1.125rem;
            color: white;
          }
          
          .day-date {
            font-size: 1rem;
            color: #9ca3af;
          }
          
          .weather-icon {
            width: 120px;
            height: 120px;
            object-fit: contain;
            margin: 0 auto;
            pointer-events: none;
            user-select: none;
            -webkit-user-drag: none;
            -khtml-user-drag: none;
            -moz-user-drag: none;
            -o-user-drag: none;
            user-drag: none;
          }
          
          .weather-info {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
          
          .condition-text {
            font-size: 1.125rem;
            color: white;
            font-weight: 500;
          }
          
          .precipitation {
            font-size: 1rem;
            color: #60a5fa;
          }
          
          .wind-info {
            text-align: center;
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
          
          .wind-speed {
            font-size: 1rem;
            color: #9ca3af;
          }
          
          .wind-direction {
            font-size: 1rem;
            color: #9ca3af;
          }
          
          .precipitation-chance {
            text-align: center;
            font-size: 1rem;
            color: #3b82f6;
          }
          
          .precipitation-amount {
            text-align: center;
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
          
          .precip-cm {
            font-size: 1rem;
            color: #9ca3af;
          }
          
          .precip-in {
            font-size: 1rem;
            color: #9ca3af;
          }
          
          .aqi-info {
            text-align: center;
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
          
          .aqi-label {
            font-size: 0.95rem;
            color: #9ca3af;
          }
          
          .aqi-value {
            font-size: 1rem;
            font-weight: 500;
          }
          
          .aqi-category {
            font-size: 0.85rem;
            font-weight: 400;
          }
          
          .uv-info {
            text-align: center;
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
          
          .uv-label {
            font-size: 0.95rem;
            color: #9ca3af;
          }
          
          .uv-value {
            font-size: 1rem;
            color: #ffc0cb;
          }
          
          .temp-info {
            text-align: left;
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
          
          .temp-high {
            font-size: 1.125rem;
            font-weight: 600;
            color: white;
          }
          
          .temp-low {
            font-size: 1rem;
            color: #9ca3af;
          }
          
          .mobile-forecast-scroll {
            display: flex;
            overflow-x: auto;
            gap: 0.75rem;
            padding: 0.75rem;
            scrollbar-width: none;
            -ms-overflow-style: none;
            -webkit-overflow-scrolling: touch;
            cursor: grab;
            user-select: none;
          }
          
          .mobile-forecast-scroll:active {
            cursor: grabbing;
          }
          
          .mobile-forecast-scroll::-webkit-scrollbar {
            display: none;
          }
          
          .mobile-forecast-card {
            background: black;
            border-radius: 0.75rem;
            padding: 1rem;
            min-width: 160px;
            flex-shrink: 0;
            text-align: center;
            border: 1px solid #60a5fa;
          }
          
          .mobile-day-name {
            font-size: 0.9rem;
            font-weight: 600;
            color: white;
            margin-bottom: 0.25rem;
          }
          
          .mobile-day-date {
            font-size: 0.8rem;
            color: #9ca3af;
            margin-bottom: 0.75rem;
          }
          
          .mobile-weather-icon {
            width: 74px;
            height: 74px;
            margin: 0 auto 0.5rem auto;
            pointer-events: none;
            user-select: none;
            -webkit-user-drag: none;
            -khtml-user-drag: none;
            -moz-user-drag: none;
            -o-user-drag: none;
            user-drag: none;
          }
          
          .mobile-condition {
            font-size: 0.8rem;
            color: white;
            font-weight: 500;
            margin-bottom: 0.75rem;
            line-height: 1.2;
          }
          
          .mobile-temp-high {
            font-size: 1rem;
            font-weight: 600;
            color: white;
            margin-bottom: 0.25rem;
          }
          
          .mobile-temp-low {
            font-size: 0.85rem;
            color: #9ca3af;
            margin-bottom: 0.75rem;
          }
          
          .mobile-details {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            font-size: 0.75rem;
          }
          
          .mobile-detail-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .mobile-detail-label {
            color: #9ca3af;
          }
          
          .mobile-detail-value {
            color: white;
            font-weight: 500;
          }
          
          .mobile-rain {
            color: #3b82f6;
          }
          
          .mobile-snow {
            color: #60a5fa;
          }
          
          .mobile-uv {
            color: #ffc0cb;
          }
          
          .mobile-aqi {
            font-weight: 500;
          }
          
          .mobile-scroll-hint {
            text-align: center;
            color: #9ca3af;
            font-size: 0.8rem;
            margin-top: 0.5rem;
          }
          
          /* Hide mobile layout on desktop */
          @media (min-width: 769px) {
            .mobile-forecast-scroll {
              display: none;
            }
            
            .mobile-scroll-hint {
              display: none;
            }
            
            .forecast-row {
              display: none;
            }
            
            .desktop-forecast-scroll {
              display: flex !important;
              overflow-x: auto;
              gap: 1.25rem;
              padding: 1.25rem;
              scrollbar-width: none;
              -ms-overflow-style: none;
              -webkit-overflow-scrolling: touch;
              cursor: grab;
              user-select: none;
            }
            
            .desktop-forecast-scroll:active {
              cursor: grabbing;
            }
            
            .desktop-forecast-scroll::-webkit-scrollbar {
              display: none;
            }
            
            .desktop-forecast-card {
              background: black;
              border-radius: 1rem;
              padding: 1.5rem;
              min-width: 240px;
              flex-shrink: 0;
              text-align: center;
              border: 1px solid #60a5fa;
            }
            
            .desktop-day-name {
              font-size: 1.35rem;
              font-weight: 600;
              color: white;
              margin-bottom: 0.375rem;
            }
            
            .desktop-day-date {
              font-size: 1.2rem;
              color: #9ca3af;
              margin-bottom: 1rem;
            }
            
            .desktop-weather-icon {
              width: 140px;
              height: 140px;
              margin: 0 auto 0.75rem auto;
              pointer-events: none;
              user-select: none;
              -webkit-user-drag: none;
              -khtml-user-drag: none;
              -moz-user-drag: none;
              -o-user-drag: none;
              user-drag: none;
            }
            
            .desktop-condition {
              font-size: 1.2rem;
              color: white;
              font-weight: 500;
              margin-bottom: 1rem;
              line-height: 1.2;
            }
            
            .desktop-temp-high {
              font-size: 1.5rem;
              font-weight: 600;
              color: white;
              margin-bottom: 0.375rem;
            }
            
            .desktop-temp-low {
              font-size: 1.275rem;
              color: #9ca3af;
              margin-bottom: 1rem;
            }
            
            .desktop-details {
              display: flex;
              flex-direction: column;
              gap: 0.75rem;
              font-size: 1.125rem;
            }
            
            .desktop-detail-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            
            .desktop-detail-label {
              color: #9ca3af;
            }
            
            .desktop-detail-value {
              color: white;
              font-weight: 500;
            }
            
            .desktop-rain {
              color: #3b82f6;
            }
            
            .desktop-snow {
              color: #60a5fa;
            }
            
            .desktop-uv {
              color: #ffc0cb;
            }
            
            .desktop-aqi {
              font-weight: 500;
            }
            
            .desktop-scroll-hint {
              display: block !important;
              text-align: center;
              color: #9ca3af;
              font-size: 1.2rem;
              margin-top: 0.75rem;
            }
          }
          
          /* Mobile responsive */
          @media (max-width: 768px) {
            .forecast-container {
              padding: 0;
            }
            
            .forecast-row {
              display: none;
            }
          }
          
          /* Extra small screens */
          @media (max-width: 480px) {
            .forecast-row {
              grid-template-columns: ${gridTemplates.small};
              padding: 0.5rem;
            }
            
            .weather-icon {
              width: 60px;
              height: 60px;
            }
            
            .condition-text {
              font-size: 0.925rem;
              line-height: 1.2;
            }
          }
        `}
      </style>
      
      <h3 className="forecast-title">Daily Forecast</h3>
      
      <div className="forecast-container">
        {/* Desktop Scrollable Card Layout */}
        <div 
          className="desktop-forecast-scroll"
          style={{display: 'none'}}
          onMouseDown={(e) => {
            const scrollContainer = e.currentTarget;
            scrollContainer.style.cursor = 'grabbing';
            scrollContainer.style.scrollBehavior = 'auto';
            let isDown = true;
            let startX = e.pageX;
            let scrollLeft = scrollContainer.scrollLeft;

            const handleMouseMove = (e) => {
              if (!isDown) return;
              e.preventDefault();
              const x = e.pageX;
              const walk = startX - x;
              scrollContainer.scrollLeft = scrollLeft + walk;
            };

            const handleMouseUp = () => {
              isDown = false;
              scrollContainer.style.cursor = 'grab';
              scrollContainer.style.scrollBehavior = 'smooth';
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
          onTouchStart={(e) => {
            const scrollContainer = e.currentTarget;
            scrollContainer.style.scrollBehavior = 'auto';
          }}
          onTouchEnd={(e) => {
            const scrollContainer = e.currentTarget;
            scrollContainer.style.scrollBehavior = 'smooth';
          }}
        >
          {forecastDays.map((day, index) => {
            const isToday = index === 0; // FIXED: First day is always "Today"
            const rainChance = day.day.daily_chance_of_rain || 0;
            const snowChance = day.day.daily_chance_of_snow || 0;
            const rainAmount = day.day.rainAccumulation || 0;
            const snowAmount = day.day.snowAccumulation || 0;
            const windSpeedMph = day.day.maxWindSpeed || 0;
            const windDir = getWindDirection(day.day.windDir || 0);
            const showUvIndex = index < 4; // Show UV index for today and next 3 days (4 total)
            const aqiData = getAQIForDate(day.date);
            
            return (
              <div key={`desktop-${day.date}`} className="desktop-forecast-card">
                <div className="desktop-day-name">
                  {isToday ? 'Today' : getDayName(day.date)}
                </div>
                <div className="desktop-day-date">
                  {formatDate(day.date)}
                </div>
                
                <img 
                  src={getWeatherIconFixedLocal(day.day.condition.text, 1)}
                  alt={day.day.condition.text}
                  className="desktop-weather-icon"
                />
                
                <div className="desktop-condition">
                  {formatCondition(day.day.condition.text)}
                </div>
                
                <div className="desktop-temp-high">
                  {Math.round(day.day.maxtemp_c)}°C / {Math.round(day.day.maxtemp_f)}°F
                </div>
                <div className="desktop-temp-low">
                  {Math.round(day.day.mintemp_c)}°C / {Math.round(day.day.mintemp_f)}°F
                </div>
                
                <div className="desktop-details">
                  <div className="desktop-detail-row">
                    <span className="desktop-detail-label">Wind</span>
                    <span className="desktop-detail-value">
                      {Math.round(windSpeedMph)} mph {windDir}
                    </span>
                  </div>
                  
                  <div className="desktop-detail-row">
                    <span className="desktop-detail-label"></span>
                    <span className="desktop-detail-value">
                      {Math.round(windSpeedMph * 1.609)} kmh {windDir}
                    </span>
                  </div>
                  
                  {index < 4 && (
                    <div className="desktop-detail-row">
                      <span className="desktop-detail-label">Air Quality</span>
                      <span className="desktop-detail-value desktop-aqi" style={{color: aqiData.color}}>
                        {aqiData.value && aqiData.category ? `${aqiData.category} (${aqiData.value})` : ''}
                      </span>
                    </div>
                  )}
                  
                  {showUvIndex && (
                    <div className="desktop-detail-row">
                      <span className="desktop-detail-label">UV Index</span>
                      <span className="desktop-detail-value desktop-uv">
                        {day.day.uvIndex > 0 ? day.day.uvIndex : ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="desktop-scroll-hint" style={{display: 'none'}}>
          Drag left to see more days →
        </div>

        {/* Mobile Scrollable Card Layout */}
        <div 
          className="mobile-forecast-scroll"
          onMouseDown={(e) => {
            const scrollContainer = e.currentTarget;
            scrollContainer.style.cursor = 'grabbing';
            scrollContainer.style.scrollBehavior = 'auto';
            let isDown = true;
            let startX = e.pageX;
            let scrollLeft = scrollContainer.scrollLeft;

            const handleMouseMove = (e) => {
              if (!isDown) return;
              e.preventDefault();
              const x = e.pageX;
              const walk = startX - x;
              scrollContainer.scrollLeft = scrollLeft + walk;
            };

            const handleMouseUp = () => {
              isDown = false;
              scrollContainer.style.cursor = 'grab';
              scrollContainer.style.scrollBehavior = 'smooth';
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
          onTouchStart={(e) => {
            const scrollContainer = e.currentTarget;
            scrollContainer.style.scrollBehavior = 'auto';
          }}
          onTouchEnd={(e) => {
            const scrollContainer = e.currentTarget;
            scrollContainer.style.scrollBehavior = 'smooth';
          }}
        >
          {forecastDays.map((day, index) => {
            const isToday = index === 0; // FIXED: First day is always "Today"
            const rainChance = day.day.daily_chance_of_rain || 0;
            const snowChance = day.day.daily_chance_of_snow || 0;
            const rainAmount = day.day.rainAccumulation || 0;
            const snowAmount = day.day.snowAccumulation || 0;
            const windSpeedMph = day.day.maxWindSpeed || 0;
            const windDir = getWindDirection(day.day.windDir || 0);
            const showUvIndex = index < 4;
            const aqiData = getAQIForDate(day.date);
            
            return (
              <div key={`mobile-${day.date}`} className="mobile-forecast-card">
                <div className="mobile-day-name">
                  {isToday ? 'Today' : getDayName(day.date)}
                </div>
                <div className="mobile-day-date">
                  {formatDate(day.date)}
                </div>
                
                <img 
                  src={getWeatherIconFixedLocal(day.day.condition.text, 1)}
                  alt={day.day.condition.text}
                  className="mobile-weather-icon"
                />
                
                <div className="mobile-condition">
                  {formatCondition(day.day.condition.text)}
                </div>
                
                <div className="mobile-temp-high">
                  {Math.round(day.day.maxtemp_c)}°C / {Math.round(day.day.maxtemp_f)}°F
                </div>
                <div className="mobile-temp-low">
                  {Math.round(day.day.mintemp_c)}°C / {Math.round(day.day.mintemp_f)}°F
                </div>
                
                <div className="mobile-details">
                  <div className="mobile-detail-row">
                    <span className="mobile-detail-label">Wind</span>
                    <span className="mobile-detail-value">
                      {Math.round(windSpeedMph)} mph {windDir}
                    </span>
                  </div>
                  
                  <div className="mobile-detail-row">
                    <span className="mobile-detail-label"></span>
                    <span className="mobile-detail-value">
                      {Math.round(windSpeedMph * 1.609)} kmh {windDir}
                    </span>
                  </div>
                  
                  {index < 4 && (
                    <div className="mobile-detail-row">
                      <span className="mobile-detail-label">Air Quality</span>
                      <span className="mobile-detail-value mobile-aqi" style={{color: aqiData.color}}>
                        {aqiData.value && aqiData.category ? `${aqiData.category} (${aqiData.value})` : 'No Data'}
                      </span>
                    </div>
                  )}
                  
                  {showUvIndex && (
                    <div className="mobile-detail-row">
                      <span className="mobile-detail-label">UV Index</span>
                      <span className="mobile-detail-value mobile-uv">
                        {day.day.uvIndex > 0 ? day.day.uvIndex : ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mobile-scroll-hint">
          Swipe left to see more days →
        </div>
      </div>
    </div>
  );
};

export default DailyForecast;